/* ---------------------------------------------------------------------------
   The activities a bridge is built in — the "prepared library" Proposal 1 asks
   for, so that an entry takes little time on site.

   WHAT THIS IS FOR. FWO's concept note of 7 September 2026, §3.2: site staff
   record updates through a Site Progress application; "a photograph, site record
   or change note is uploaded against the element it concerns, at the time it is
   made", and "activities are selected from a prepared library (casting, pouring,
   erection, inspection, and so on) so that an entry takes little time on site."
   This is that library. Three taps on a riverbank — element, activity, camera —
   is the whole design brief, and every decision below serves it.

   PROVENANCE — read this before adding to the list.

   These are the operations named by the construction sequence the vendored
   modeller's own `schedule/ScheduleExtractor.ts` walks, generalised from the
   component tree it seeds activities from:

     per bent        pile caps and piles → columns → bent cap → bearing lines
     per abutment    footing → wall and wing walls → back wall → approach slab
     per span        deck section → girders and diaphragms → side walls →
                     road surface → lane dividers
     bridge-wide     road surfaces → lighting poles → drainage

   That sequence is a real artefact in this repository rather than a list
   invented here, which is the same standard `src/data/elements.ts` holds itself
   to. What this file adds is the *verb* — the sequence says a bent cap is built
   after the columns; it does not say that building one means fixing steel, then
   formwork, then a pour, then curing, then stripping. Those five are what a
   foreman reports, and they are what the library has to offer.

   **THE LIBRARY IS NOT PER TENANT.** Pouring concrete is pouring concrete in
   Rawalpindi and in Syangja. This follows the rule at the top of
   src/tenant/types.ts: the element list, the condition scales, the defect
   vocabulary and the quantity methods are the same for every department, and a
   capability behind a profile flag is a capability only one department's users
   ever test. Where a department's own method statement disagrees, the answer is
   a documented divergence with a source beside it, not a configurable list.

   **IT IS ALSO NOT A SCHEDULE.** There are no durations, no dependencies and no
   dates here. The modeller has a CPM solver with a P6-style Gantt and XER import
   and export (`schedule/Schedule.ts`), the schedule rides inside
   `App.SerializeModelState` and is therefore already in R2, and none of that is
   this. A progress event says *this happened, here, on this date, and here is the
   photograph*; what it means for the programme is computed from the record
   afterwards, in the office, which is exactly the division of labour §3.2
   describes. Keeping the two apart is what lets a site entry be three taps.

   Ids are stable and stored in D1. Renaming a `label` is free; changing an `id`
   is a migration — see RETIRED_ACTIVITIES.
   --------------------------------------------------------------------------- */

/**
 * The stage of work an activity belongs to. Groups the picker on screen.
 *
 * Ordered as the work is done, because the picker is used in the field by
 * somebody who has just done one of them and is looking for it — not browsing a
 * taxonomy. A crew that has just strvck formwork finds "Formwork struck" under
 * the same heading as the pour it followed.
 */
export type ActivityGroup =
  | 'Setting out and earthworks'
  | 'Foundations'
  | 'Substructure'
  | 'Superstructure'
  | 'Finishes and furniture'
  | 'Checks and events';

export interface ConstructionActivity {
  /** Stable id, stored in D1. Never reused. */
  id: string;
  /** As the picker prints it. */
  label: string;
  group: ActivityGroup;
  /**
   * What a quantity against this activity would be measured in, or null.
   *
   * NULL IS COMMON AND IS NOT AN OMISSION. "Formwork struck" and "Survey check"
   * have no natural quantity, and offering a box for one invites a number
   * nobody can interpret. Where a unit is given, the field is still optional:
   * a foreman who poured "the north half of the bent cap" and did not measure
   * it should be able to say the pour happened.
   */
  unit: 'm³' | 'm²' | 'm' | 't' | 'nos' | null;
  /** One sentence, where the label alone would be ambiguous. */
  note?: string;
}

/**
 * The library, in the order the picker draws it.
 *
 * DELIBERATELY SHORT. Twenty-six entries, not a hundred: this is a list somebody
 * scrolls one-handed in sunlight, and a library long enough to need a search box
 * is a library that has stopped saving time on site. Where a crew needs to say
 * something the list does not carry, the note field on the event carries it —
 * and a note that keeps recurring is the argument for a twenty-seventh entry.
 */
export const CONSTRUCTION_ACTIVITIES: readonly ConstructionActivity[] = [
  /* --- setting out and earthworks --- */
  { id: 'setting-out', label: 'Setting out', group: 'Setting out and earthworks', unit: null },
  { id: 'excavation', label: 'Excavation', group: 'Setting out and earthworks', unit: 'm³' },
  {
    id: 'dewatering',
    label: 'Dewatering',
    group: 'Setting out and earthworks',
    unit: null,
    note: 'Recorded as an event because it governs whether work below can proceed, not as a quantity.',
  },
  { id: 'backfill', label: 'Backfill and compaction', group: 'Setting out and earthworks', unit: 'm³' },

  /* --- foundations --- */
  { id: 'pile-bored', label: 'Pile bored', group: 'Foundations', unit: 'm' },
  { id: 'pile-cage', label: 'Pile cage lowered', group: 'Foundations', unit: 't' },
  { id: 'pile-concreted', label: 'Pile concreted', group: 'Foundations', unit: 'm³' },
  {
    id: 'pile-test',
    label: 'Pile integrity or load test',
    group: 'Foundations',
    unit: 'nos',
    note: 'The result belongs in the note or in an attached record; this says the test was carried out.',
  },
  { id: 'pilecap-poured', label: 'Pile cap poured', group: 'Foundations', unit: 'm³' },
  { id: 'footing-poured', label: 'Footing poured', group: 'Foundations', unit: 'm³' },

  /* --- substructure --- */
  { id: 'rebar-fixed', label: 'Reinforcement fixed', group: 'Substructure', unit: 't' },
  { id: 'formwork-erected', label: 'Formwork erected', group: 'Substructure', unit: 'm²' },
  { id: 'concrete-poured', label: 'Concrete poured', group: 'Substructure', unit: 'm³' },
  {
    id: 'concrete-cured',
    label: 'Curing complete',
    group: 'Substructure',
    unit: null,
    note: 'The end of the curing period, which is what releases the next operation.',
  },
  { id: 'formwork-struck', label: 'Formwork struck', group: 'Substructure', unit: 'm²' },
  { id: 'bearing-set', label: 'Bearings set', group: 'Substructure', unit: 'nos' },

  /* --- superstructure --- */
  { id: 'girder-cast', label: 'Girder cast', group: 'Superstructure', unit: 'nos' },
  {
    id: 'girder-stressed',
    label: 'Girder stressed',
    group: 'Superstructure',
    unit: 'nos',
    note: 'Pre-tensioning or first-stage post-tensioning, as the method statement has it.',
  },
  { id: 'girder-erected', label: 'Girder erected', group: 'Superstructure', unit: 'nos' },
  { id: 'diaphragm-cast', label: 'Diaphragm cast', group: 'Superstructure', unit: 'nos' },
  { id: 'deck-poured', label: 'Deck slab poured', group: 'Superstructure', unit: 'm³' },
  { id: 'post-tensioned', label: 'Post-tensioning and grouting', group: 'Superstructure', unit: 'nos' },

  /* --- finishes --- */
  { id: 'joint-installed', label: 'Expansion joint installed', group: 'Finishes and furniture', unit: 'm' },
  { id: 'wearing-course', label: 'Wearing course laid', group: 'Finishes and furniture', unit: 'm²' },
  { id: 'parapet-built', label: 'Parapet or railing built', group: 'Finishes and furniture', unit: 'm' },
  { id: 'drainage-installed', label: 'Drainage installed', group: 'Finishes and furniture', unit: 'nos' },

  /* --- checks and events ---
     THE FOUR THAT ARE NOT WORK. A hold point and a survey check are things that
     happened to the structure and govern what may happen next; a non-conformance
     and a stoppage are the two a monthly report is actually read for. Recording
     them in the same stream as the pours is what makes the record a construction
     record rather than a production tally. */
  {
    id: 'hold-point',
    label: 'Hold point released',
    group: 'Checks and events',
    unit: null,
    note: 'An inspection the works could not proceed past. Say who released it in the note.',
  },
  { id: 'survey-check', label: 'Survey check', group: 'Checks and events', unit: null },
  {
    id: 'non-conformance',
    label: 'Non-conformance raised',
    group: 'Checks and events',
    unit: null,
    note: 'What was found and what is proposed belong in the note; an issue on the finished structure is an issue, not this.',
  },
  {
    id: 'stoppage',
    label: 'Work stopped',
    group: 'Checks and events',
    unit: null,
    note: 'Weather, access, materials, instruction. The reason is the whole value of the entry.',
  },
];

/** The groups, in the order the picker draws them. */
export const ACTIVITY_GROUPS: readonly ActivityGroup[] = [
  'Setting out and earthworks',
  'Foundations',
  'Substructure',
  'Superstructure',
  'Finishes and furniture',
  'Checks and events',
];

/**
 * Ids that once existed and no longer do.
 *
 * Empty today and kept so it is obvious where a retirement goes. A stored event
 * naming a retired id must still render — see `activityLabel`, which never
 * returns an empty string for something the database holds.
 */
export const RETIRED_ACTIVITIES: Readonly<Record<string, string>> = {};

const BY_ID = new Map(CONSTRUCTION_ACTIVITIES.map((a) => [a.id, a]));

export function activityById(id: string): ConstructionActivity | null {
  return BY_ID.get(id) ?? null;
}

/**
 * What to print for a stored id.
 *
 * NEVER EMPTY, and never the raw id alone. A row written by a build that knew an
 * activity this one does not is still a thing somebody did on a bridge; it
 * renders as the id with a mark rather than vanishing from the record or
 * printing as a blank cell on a monthly report.
 */
export function activityLabel(id: string): string {
  const known = BY_ID.get(id);
  if (known) return known.label;
  const retired = RETIRED_ACTIVITIES[id];
  if (retired) return `${retired} (retired)`;
  return `${id} (unknown activity)`;
}

export function isKnownActivity(id: string): boolean {
  return BY_ID.has(id);
}

/** The unit a quantity against this activity is measured in, or null. */
export function activityUnit(id: string): ConstructionActivity['unit'] {
  return BY_ID.get(id)?.unit ?? null;
}

/** The library, grouped, for a picker. Groups with nothing in them are dropped. */
export function activitiesByGroup(): readonly {
  group: ActivityGroup;
  activities: readonly ConstructionActivity[];
}[] {
  return ACTIVITY_GROUPS.map((group) => ({
    group,
    activities: CONSTRUCTION_ACTIVITIES.filter((a) => a.group === group),
  })).filter((g) => g.activities.length > 0);
}
